import React, { useState } from "react";

import axios from "axios";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import AlertDialog from "@/src/components/dialog/AlertDialog";
import { Button } from "@/src/components/ui/button";
import { FieldError } from "@/src/components/ui/field";
import { Skeleton } from "@/src/components/ui/skeleton";
import { useOrganizationFontMutations } from "@/src/hooks/mutations/useOrganizationFontMutations";
import useFontFaceGroupLabel from "@/src/hooks/useFontFaceGroupLabel";
import { groupFaceListByDescriptor } from "@/src/utils/fontFaces";

import AddFontDialog from "./AddFontDialog";

import type { AxiosError } from "axios";
import type { JSX, ReactNode } from "react";

import type { FontFamily } from "@/src/api/StylesAPI.types";

const DELETE_CONFLICT_STATUS = 409;

interface DeleteConflictData {
  projects?: string[];
}

function readConflictProjectList(error: unknown): string[] | null {
  if (!axios.isAxiosError(error)) return null;

  const { response } = error as AxiosError<DeleteConflictData>;
  if (response?.status !== DELETE_CONFLICT_STATUS) return null;

  return response.data?.projects ?? [];
}

interface OrganizationFontRowProps {
  family: FontFamily;
  onDelete: (family: FontFamily) => void;
}

function OrganizationFontRow({
  family,
  onDelete,
}: OrganizationFontRowProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const buildGroupLabel = useFontFaceGroupLabel();

  const handleDeleteClick = (): void => {
    onDelete(family);
  };

  return (
    <div className="border-border flex items-center justify-between rounded-lg border p-4">
      <div className="flex flex-col">
        <span style={{ fontFamily: family.css_stack }}>
          {family.display_name}
        </span>
        <span className="text-muted-foreground text-xs">
          {groupFaceListByDescriptor(family.faces)
            .map(buildGroupLabel)
            .join(" · ")}
        </span>
      </div>
      <Button
        onClick={handleDeleteClick}
        type="button"
        variant="ghost"
        aria-label={t("deleteFontAriaLabel", {
          fontName: family.display_name,
        })}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

interface OrganizationFontListProps {
  familyList: FontFamily[];
  isLoading: boolean;
  onDeleted: (fontUuid: string) => void;
}

function OrganizationFontList({
  familyList,
  isLoading,
  onDeleted,
}: OrganizationFontListProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const { deleteFont } = useOrganizationFontMutations();

  const [conflictProjectList, setConflictProjectList] = useState<
    string[] | null
  >(null);
  const [pendingDeleteFamily, setPendingDeleteFamily] =
    useState<FontFamily | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const organizationFamilyList = familyList.filter(
    (family) => family.scope === "organization",
  );

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDeleteFamily) return;

    const fontUuid = pendingDeleteFamily.uuid;
    setIsDeleteDialogOpen(false);
    setConflictProjectList(null);
    try {
      await deleteFont(fontUuid);
      onDeleted(fontUuid);
      toast.success(t("deleteSuccess"));
    } catch (error) {
      const projectList = readConflictProjectList(error);
      if (projectList) {
        setConflictProjectList(projectList);
        return;
      }
      toast.error(t("deleteError"));
    }
  };

  const handleDeleteRequest = (family: FontFamily): void => {
    setPendingDeleteFamily(family);
    setIsDeleteDialogOpen(true);
  };

  const handleUploaded = (): void => {
    setConflictProjectList(null);
  };

  const renderFamilyList = (): ReactNode => {
    if (isLoading) {
      return (
        <Skeleton className="h-16 w-full" data-testid="font-list-loading" />
      );
    }

    if (organizationFamilyList.length === 0) {
      return (
        <p className="text-muted-foreground text-sm">
          {t("noOrganizationFont")}
        </p>
      );
    }

    return organizationFamilyList.map((family) => (
      <OrganizationFontRow
        key={family.uuid}
        family={family}
        onDelete={handleDeleteRequest}
      />
    ));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-foreground text-xs font-semibold">
          {t("myFonts")}
        </h2>
        <AddFontDialog onUploaded={handleUploaded} />
      </div>
      {conflictProjectList ? (
        <FieldError>
          {t("deleteInUse", { projects: conflictProjectList.join(", ") })}
        </FieldError>
      ) : null}
      {renderFamilyList()}
      <AlertDialog
        cancelLabel={t("cancel")}
        confirmLabel={t("deleteFont")}
        confirmVariant="destructive"
        description={t("deleteConfirmDescription")}
        isOpen={isDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onOpenChange={setIsDeleteDialogOpen}
        title={t("deleteConfirmTitle", {
          fontName: pendingDeleteFamily?.display_name ?? "",
        })}
      />
    </div>
  );
}

export default OrganizationFontList;
